// src/components/reporting/cohort/CohortMultiSelect.js - compact cohort picker.
// A trigger button opens a popup checkbox list (searchable, scrollable) so the
// control's footprint stays constant as the cohort list grows; once closed only
// the selected cohorts remain on screen as chips.
//
// `single` turns it into a one-cohort picker (Cohort Themes): picking a cohort
// replaces the selection and closes the popup, and the trigger shows its name
// in place of chips. selectedIds/onChange keep the same array shape.
import { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CaretDown, X } from '@phosphor-icons/react';
import '../../../styles/activityReporting.css';

const byName = (a, b) => (a.name || '').localeCompare(b.name || '');

const CohortMultiSelect = ({ cohorts, selectedIds, onChange, disabled, single = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const sorted = useMemo(() => [...cohorts].sort(byName), [cohorts]);

  const selected = useMemo(
    () => sorted.filter((c) => selectedIds.includes(String(c.id))),
    [sorted, selectedIds]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sorted.filter((c) => (c.name || '').toLowerCase().includes(q)) : sorted;
  }, [sorted, query]);

  const toggle = (id) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  const pick = (id) => {
    onChange([id]);
    setOpen(false);
    setQuery('');
  };

  let summary;
  if (single) {
    summary = selected[0]?.name || 'Select a cohort';
  } else {
    summary = selected.length === 0
      ? 'Select cohorts'
      : `${selected.length} cohort${selected.length === 1 ? '' : 's'} selected`;
  }

  return (
    <div className="arp-ms" ref={containerRef}>
      <div className="arp-filter-label-row">
        <span className="arp-filter-label">{single ? 'Cohort' : 'Cohorts'}</span>
        {!single && selected.length > 0 && (
          <button type="button" className="arp-filter-clear" onClick={() => onChange([])}>
            Clear
          </button>
        )}
      </div>

      <button
        type="button"
        className="arp-ms-trigger"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span>{summary}</span>
        <CaretDown size={12} weight="bold" />
      </button>

      {open && (
        <div className="arp-ms-panel">
          <input
            type="text"
            className="arp-ms-search"
            placeholder="Search cohorts…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="arp-ms-list" role="group" aria-label="Filter by cohort">
            {visible.length === 0 ? (
              <span className="arp-ms-empty">No matching cohorts.</span>
            ) : visible.map((c) => (
              <label key={c.id} className="arp-filter-checkbox">
                <input
                  type={single ? 'radio' : 'checkbox'}
                  checked={selectedIds.includes(String(c.id))}
                  onChange={() => (single ? pick(String(c.id)) : toggle(String(c.id)))}
                />
                {c.name} ({c.memberCount} participant{c.memberCount === 1 ? '' : 's'})
              </label>
            ))}
          </div>
        </div>
      )}

      {!single && selected.length > 0 && (
        <div className="arp-ms-chips">
          {selected.map((c) => (
            <span key={c.id} className="arp-ms-chip">
              {c.name}
              <button
                type="button"
                className="arp-ms-chip-remove"
                onClick={() => toggle(String(c.id))}
                aria-label={`Remove ${c.name}`}
              >
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

CohortMultiSelect.propTypes = {
  cohorts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    memberCount: PropTypes.number
  })).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  single: PropTypes.bool
};

export default CohortMultiSelect;
